import autoTable from "jspdf-autotable";
import { convertAmountToWords } from "./NumberWordHelper";

const SalesInvoiceDetailedLayoutPrint = (
  doc,
  pageWidth,
  pageHeight,
  invoiceData,
  REPORT_FOOTER_IMAGE,
  REPORT_HEADER_IMAGE,
  COMPANY_NAME,
  VAT_PERCENTAGE,
  qrCodeImage,
  reportSealImage,
  reportWatermarkImage,
  layoutOptions = {},
) => {
  const {
    showHeaderImage = true,
    showFooterImage = true,
    showWatermark = true,
    showCodeColumn = true,
    headerStartY = 0,
    headerMargin: headerMarginProp,
    footerHeight: footerHeightProp,
  } = layoutOptions;

  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

  if (!invoiceData || invoiceData.length === 0) return;

  const firstItem = invoiceData[0];
  const isVatApplied = firstItem.deliv_site === "VAT Applied";
  const vatPercent = isVatApplied ? Number(VATPERCENTAGE || 0) : 0;
  const vatRate = vatPercent / 100;

  const headerMargin = headerMarginProp !== undefined ? headerMarginProp : 12;
  const headerHeight = 30;
  const margin = 10;

  let footerHeight = 30;
  if (footerHeightProp !== undefined) {
    footerHeight = footerHeightProp;
  } else if (REPORT_FOOTER_IMAGE) {
    try {
      const imgProps = doc.getImageProperties(REPORT_FOOTER_IMAGE);
      const footerWidth = pageWidth - 2 * headerMargin;
      footerHeight = (footerWidth / imgProps.width) * imgProps.height;
    } catch (e) {
      console.warn("Could not calculate footer image properties", e);
    }
  }

  const sectionHeight = 25;
  const signatureHeight = 30;

  const remarksContentWidth = (pageWidth - 2 * margin) * 0.70;
  let remarksHeight = 0;
  if (firstItem?.terms) {
    const remarksLines = doc.splitTextToSize(
      `Remarks : ${firstItem.terms}`,
      remarksContentWidth
    );
    remarksHeight = remarksLines.length * 4 + 4;
  }
  const bottomBlockHeight = sectionHeight + signatureHeight + remarksHeight + (showHeaderImage ? 25 : 10);

  const companyTRN =
    CURRENT_USER_OBJECT[0]?.trn || CURRENT_USER_OBJECT[0]?.TRN || "-";

  const drawHeaderSection = (pdfDoc) => {
    let currentY = headerStartY;

    if (showHeaderImage && REPORT_HEADER_IMAGE) {
      pdfDoc.addImage(
        REPORT_HEADER_IMAGE,
        "JPEG",
        headerMargin,
        currentY,
        pageWidth - 2 * headerMargin,
        headerHeight,
      );
      currentY += headerHeight + 5;
    }

    pdfDoc.setFontSize(16);
    pdfDoc.setFont(undefined, "bold");
    pdfDoc.text(
      isVatApplied ? "TAX INVOICE" : "COMMERCIAL INVOICE",
      pageWidth / 2,
      currentY,
      { align: "center" },
    );
    currentY += 6;

    if (isVatApplied) {
      pdfDoc.setFontSize(10);
      pdfDoc.setFont(undefined, "normal");
      pdfDoc.text(`TRN NO : ${companyTRN}`, pageWidth / 2, currentY, {
        align: "center",
      });
    }
    currentY += 5;
    pdfDoc.setFontSize(10);

    const boxGap = 2;
    const contentWidth = pageWidth - 2 * margin;
    const leftBoxWidth = contentWidth * 0.65;
    const rawClientName = firstItem.custref || firstItem.tr_desc || "Client Name";
    const wrappedClientLines = pdfDoc.splitTextToSize(rawClientName, leftBoxWidth - 4);

    const rawAddress = [firstItem.address1, firstItem.address2]
      .filter((addr) => addr && addr.trim() !== "")
      .join(", ");
    const wrappedAddressLines = pdfDoc.splitTextToSize(rawAddress, leftBoxWidth - 4);

    const leftTextLines = [
      ...wrappedClientLines,
      ...wrappedAddressLines,
      `TEL: ${firstItem.tel || "-"}`,
      `TRN: ${firstItem.governor || "-"}`,
    ];

    const lineHeightLeft = 5.5;
    const leftBoxHeight = leftTextLines.length * lineHeightLeft + 2;
    const rightBoxWidth = contentWidth * 0.35;

    const doNoLines = pdfDoc.splitTextToSize(
      firstItem.do_data || "-",
      rightBoxWidth - 22
    );

    const rightTextLines = [
      `Invoice No: ${firstItem.inv_no || "-"}`,
      `Date: ${firstItem.inv_date
        ? new Date(firstItem.inv_date).toLocaleDateString("en-GB")
        : "-"
      }`,
      ...(firstItem.lpo_no && firstItem.lpo_no !== "0" && firstItem.lpo_no !== 0
        ? [`LPO No: ${firstItem.lpo_no}`]
        : []),
      ...(firstItem.do_data && firstItem.do_data.trim() !== ""
        ? doNoLines.map((line, index) =>
          index === 0 ? `DO No: ${line}` : `        ${line}`
        )
        : []),
      `Salesman: ${firstItem.sale_man || "-"}`,
    ];

    const lineHeightRight = 5.5;
    const rightBoxHeight = rightTextLines.length * lineHeightRight + 2;
    const boxHeight = Math.max(leftBoxHeight, rightBoxHeight);

    let leftY = currentY + 4;
    for (let i = 0; i < leftTextLines.length; i++) {
      pdfDoc.setFont(undefined, i === 0 ? "bold" : "normal");
      pdfDoc.text(leftTextLines[i], margin + 2, leftY);
      leftY += lineHeightLeft;
    }

    const rightBoxX = margin + leftBoxWidth + boxGap;
    let rightY = currentY + 4;
    pdfDoc.setFont(undefined, "normal");
    for (let i = 0; i < rightTextLines.length; i++) {
      pdfDoc.text(rightTextLines[i], rightBoxX + 2, rightY);
      rightY += lineHeightRight;
    }

    return currentY + boxHeight + 5;
  };

  const startTableY = drawHeaderSection(doc);

  const filteredData = invoiceData.filter(
    (item) => (item.unit || item.uom || "").toUpperCase() !== "FOC"
  );

  const hasUnitColumn = filteredData.some(
    (item) => (item.unit || item.uom || "").trim() !== ""
  );

  const firstColHeader = showCodeColumn ? "Code" : "SN";

  const tableHead = isVatApplied
    ? hasUnitColumn
      ? [[firstColHeader, "Description", "Unit", "Qty", "Unit Price", "VAT%", "VAT AMT", "Amount"]]
      : [[firstColHeader, "Description", "Qty", "Unit Price", "VAT%", "VAT AMT", "Amount"]]
    : hasUnitColumn
      ? [[firstColHeader, "Description", "Unit", "Qty", "Unit Price", "VAT%", "Amount"]]
      : [[firstColHeader, "Description", "Qty", "Unit Price", "VAT%", "Amount"]];

  const lineItems = filteredData.map((item) => {
    const qty = item.tr_qty || 0;
    const unitPrice = item.funit_pric || 0;
    const lineTotal = qty * unitPrice;
    const lineVat = Math.round(lineTotal * vatPercent) / 100;
    const lineTotalWithVat = lineTotal + lineVat;
    return {
      code: item.code || item.tr_code || "",
      desc: item.idesc || item.tr_desc || "",
      uom: item.unit || item.uom || "",
      qty: qty.toString(),
      price: unitPrice.toFixed(2),
      vatPercent: vatPercent.toFixed(0),
      vatAmount: lineVat.toFixed(2),
      amount: lineTotalWithVat.toFixed(2),
    };
  });

  const tableBody = lineItems.map((item, index) => {
    const firstCol = showCodeColumn ? item.code : (index + 1).toString();
    if (isVatApplied) {
      return hasUnitColumn
        ? [firstCol, item.desc, item.uom, item.qty, item.price, item.vatPercent, item.vatAmount, item.amount]
        : [firstCol, item.desc, item.qty, item.price, item.vatPercent, item.vatAmount, item.amount];
    }
    return hasUnitColumn
      ? [firstCol, item.desc, item.uom, item.qty, item.price, item.vatPercent, item.amount]
      : [firstCol, item.desc, item.qty, item.price, item.vatPercent, item.amount];
  });

  const col0Width = showCodeColumn ? 25 : 10;
  const col1Width = hasUnitColumn
    ? (showCodeColumn ? 50 : 65)
    : (showCodeColumn ? 55 : 70);

  const columnStyles = isVatApplied
    ? {
      0: { halign: showCodeColumn ? "left" : "center", cellWidth: col0Width },
      1: { halign: "left", cellWidth: col1Width },
      ...(hasUnitColumn ? { 2: { halign: "center", cellWidth: 15 } } : {}),
      [hasUnitColumn ? 3 : 2]: { halign: "center", cellWidth: 15 },
      [hasUnitColumn ? 4 : 3]: { halign: "right", cellWidth: 20 },
      [hasUnitColumn ? 5 : 4]: { halign: "center", cellWidth: 15 },
      [hasUnitColumn ? 6 : 5]: { halign: "right", cellWidth: 20 },
      [hasUnitColumn ? 7 : 6]: { halign: "right", cellWidth: 30 },
    }
    : {
      0: { halign: showCodeColumn ? "left" : "center", cellWidth: col0Width },
      1: { halign: "left", cellWidth: col1Width },
      ...(hasUnitColumn ? { 2: { halign: "center", cellWidth: 15 } } : {}),
      [hasUnitColumn ? 3 : 2]: { halign: "center", cellWidth: 15 },
      [hasUnitColumn ? 4 : 3]: { halign: "right", cellWidth: 20 },
      [hasUnitColumn ? 5 : 4]: { halign: "center", cellWidth: 15 },
      [hasUnitColumn ? 6 : 5]: { halign: "right", cellWidth: 30 },
    };

  autoTable(doc, {
    head: tableHead,
    body: tableBody,
    startY: startTableY,
    theme: "plain",
    tableWidth: "auto",

    margin: {
      top: startTableY,
      left: margin,
      right: margin,
      bottom: footerHeight + 5,
    },

    styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
    headStyles: {
      fontStyle: "bold",
      halign: "center",
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
    },
    columnStyles,

    willDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeaderSection(doc);
      }
    },

    didDrawCell: function (data) {
      const cell = data.cell;
      doc.setDrawColor(0);
      doc.setLineWidth(0.1);

      doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
      doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);

      if (data.row.section === "head") {
        doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
        doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
      } else if (data.row.index === data.table.body.length - 1) {
        doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
      }
    },
  });

  const tableEndY = doc.lastAutoTable.finalY || startTableY;
  const contentWidth = pageWidth - 2 * margin;

  const totalExclVat = filteredData.reduce(
    (sum, item) => sum + (item.funit_pric || 0) * (item.tr_qty || 0),
    0,
  );

  const specialDiscount = firstItem.disc_amt || firstItem.fdisc_amt || 0;
  const subTotal = totalExclVat - specialDiscount;
  const roundOffValue = Number(firstItem.e) || 0;

  const advancePercent = Number(firstItem.paid_amt || 0);

  let vatAmount = 0;
  let grandTotal = 0;
  let advanceReceived = 0;

  if (advancePercent > 0) {
    advanceReceived = subTotal * (advancePercent / 100);

    vatAmount = isVatApplied
      ? Math.round(advanceReceived * vatPercent) / 100
      : 0;

    grandTotal = advanceReceived + vatAmount + roundOffValue;
  } else {
    vatAmount = isVatApplied
      ? Math.round(subTotal * vatPercent) / 100
      : 0;
    grandTotal = Number(subTotal?.toFixed(2)) + Number(vatAmount?.toFixed(2)) + Number(roundOffValue?.toFixed(2));
  }

  let totalRows = 2;
  if (Number(specialDiscount) > 0) totalRows += 2;
  if (Number(advanceReceived) > 0) totalRows += 2;
  if (isVatApplied) totalRows += 1;
  if (roundOffValue !== 0) totalRows += 1;

  const displaySectionHeight = Math.max(20, totalRows * 4.5 + 6);

  const bottomBlockNeeded = displaySectionHeight + signatureHeight + remarksHeight + 5;
  const spaceLeft = pageHeight - footerHeight - 5 - tableEndY;

  if (spaceLeft < bottomBlockNeeded) {
    doc.addPage();
    drawHeaderSection(doc);
  }

  const amountInWords = convertAmountToWords(
    grandTotal,
    firstItem.fc || "AED",
  );

  const sectionTopY = pageHeight - footerHeight - signatureHeight - displaySectionHeight - remarksHeight - 5;

  doc.rect(margin, sectionTopY, contentWidth, displaySectionHeight);

  let textY = sectionTopY + 6;
  doc.setFontSize(9);

  doc.setFont(undefined, "bolditalic");
  doc.text(amountInWords, margin + 5, textY, { maxWidth: contentWidth * 0.52 });

  let totalsY = sectionTopY + 5;
  const rightX = margin + contentWidth - 3;

  const printRightLine = (label, value, bold = false) => {
    doc.setFont(undefined, bold ? "bold" : "normal");
    doc.text(label, rightX - 58, totalsY);
    doc.text(
      Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      rightX,
      totalsY,
      { align: "right" }
    );
    totalsY += 4.5;
  };

  printRightLine("Total (Excl VAT)", totalExclVat?.toFixed(2));
  if (Number(specialDiscount) > 0) {
    printRightLine("Special Discount", specialDiscount?.toFixed(2));
    printRightLine("Sub Total", subTotal?.toFixed(2));
  }

  if (Number(advanceReceived) > 0) {
    printRightLine(
      `Advance Amount ${advancePercent}%`,
      advanceReceived?.toFixed(2)
    );
  }

  if (isVatApplied) {
    printRightLine(`VAT@${vatPercent}%`, vatAmount?.toFixed(2));
  }

  if (roundOffValue !== 0) {
    printRightLine("Round Off", roundOffValue?.toFixed(2));
  }

  printRightLine(
    "GRAND TOTAL (Incl.VAT)",
    grandTotal?.toFixed(2),
    true
  );

  if (firstItem.terms) {
    const remarksY = sectionTopY + displaySectionHeight + 4;
    const remarksMaxWidth = contentWidth * 0.70;
    doc.setFontSize(9);
    doc.setFont(undefined, "italic");
    const remarksLabel = "Remarks : ";
    doc.text(remarksLabel, margin + 4, remarksY);
    const labelW = doc.getTextWidth(remarksLabel);
    doc.text(firstItem.terms, margin + 4 + labelW, remarksY, { maxWidth: remarksMaxWidth - labelW });
  }

  const sigY = sectionTopY + displaySectionHeight + remarksHeight + signatureHeight - 5;

  doc.line(15, sigY, 80, sigY);
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  doc.text("Receiver's Signature", 45, sigY + 5, { align: "center" });

  if (reportSealImage) {
    const sealSize = 20;
    const rightSignatureCenterX = pageWidth - 45;
    const sealX = rightSignatureCenterX - (sealSize / 2);
    const sealY = sigY - sealSize;
    doc.addImage(reportSealImage, "JPEG", sealX, sealY, sealSize, sealSize);
  }

  doc.line(pageWidth - 80, sigY, pageWidth - 15, sigY);
  doc.text(`For ${COMPANY_NAME}`, pageWidth - 45, sigY + 5, { align: "center" });

  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    if (showWatermark && reportWatermarkImage && reportWatermarkImage.trim() !== "") {
      try {
        const cleanBase64 = reportWatermarkImage.replace(/\s/g, "");

        if (typeof doc.GState === "function") {
          doc.setGState(new doc.GState({ opacity: 0.05 }));
        }

        const watermarkWidth = 100;
        const watermarkHeight = 100;
        const x = (pageWidth - watermarkWidth) / 2;
        const y = (pageHeight - watermarkHeight) / 2;

        doc.addImage(cleanBase64, "JPEG", x, y, watermarkWidth, watermarkHeight);

        if (typeof doc.GState === "function") {
          doc.setGState(new doc.GState({ opacity: 1 }));
        }
      } catch (e) {
        console.warn("Could not render base64 watermark image properly:", e);
      }
    }

    if (showFooterImage && REPORT_FOOTER_IMAGE) {
      doc.addImage(
        REPORT_FOOTER_IMAGE,
        "JPEG",
        headerMargin,
        pageHeight - footerHeight - 5,
        pageWidth - 2 * headerMargin,
        footerHeight,
      );
    }

    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(
      `Page ${p} of ${totalPages}`,
      pageWidth / 2,
      showFooterImage ? pageHeight - footerHeight - 8 : pageHeight - 10,
      { align: "center" },
    );
    doc.setTextColor(0);
  }
};

export default SalesInvoiceDetailedLayoutPrint;
